import type { ActionCandidate } from "../../../../../types/candidate";
import type {
  LoopScope,
  LoopingStore,
} from "../../../../../stores/automation/looping.store";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";

/**
 * Whether an editor may offer preceding actions as data sources.
 *
 * Normally the owning `action` both identifies the editor's place in the flow
 * and bounds the list, so with no owning action there is nothing to offer --
 * that is what keeps trigger conditions to trigger fields alone.
 *
 * Conditional Logic branch conditions have no owning action yet do have
 * sources: they hand over a list already scoped to their block's execution
 * path, which `actionsArePreScoped` declares (BUG-21770).
 */
export const canOfferActionSources = (
  action: ActionCandidate | undefined,
  actionsArePreScoped?: boolean,
): boolean => action !== undefined || actionsArePreScoped === true;

/**
 * The action that bounds the offered sources: everything up to it is available,
 * it and everything after it is not.
 *
 * There is no boundary once the `actions` list is pre-scoped -- a Conditional
 * Logic branch condition's list already ends where its block begins, and using
 * the left side's resolved action instead sliced that action (and everything
 * between it and the block) back out (BUG-21770).
 */
export const actionSourceBoundary = (
  action: ActionCandidate | undefined,
  actionsArePreScoped?: boolean,
): ActionCandidate | undefined =>
  actionsArePreScoped === true ? undefined : action;

/**
 * The loop whose Current Item an editor may offer.
 *
 * Normally it is the loop enclosing the editor's own action, resolved through
 * the store. Once the caller declares its sources pre-scoped it knows better:
 * a Conditional Logic branch condition passes its BLOCK's enclosing loop,
 * because deriving one from the left side's resolved action pointed at the
 * loop's SOURCE action, which sits outside the loop (BUG-21770). `undefined`
 * is then meaningful -- that branch is not inside a loop and has no Current
 * Item.
 */
export const resolveEditorLoopScope = (
  action: ActionCandidate | undefined,
  rootLooping: LoopingStore | undefined,
  loopScope: LoopScope | undefined,
  actionsArePreScoped?: boolean,
): LoopScope | LoopingStore | undefined => {
  if (actionsArePreScoped === true) return loopScope;
  return action !== undefined
    ? (rootLooping?.scopeForAction?.(action.hash) ?? rootLooping)
    : rootLooping;
};

/**
 * The loop whose Current Item a field picker will actually offer, or undefined
 * when it offers none. Action editors read the loop ENCLOSING the open action
 * from the store (`currentLoopScope`, resolved past branch containers -- the
 * nearest-container locator is the wrong read, it hid Current Item for actions
 * inside a Conditional Logic branch inside a loop); branch conditions pass
 * their block's loop explicitly and mark it pre-scoped. Either way a scope
 * without a valid source offers nothing. Single source of truth for the
 * CurrentOption entry AND for the editors' "single menu item" shortcut, which
 * must never skip a root menu that would hold Current Item.
 */
export const resolveCurrentItemScope = (
  candidate: Pick<CandidateStore, "currentLoopScope">,
  loopScope: LoopScope | undefined,
  actionsArePreScoped?: boolean,
): LoopScope | undefined => {
  const scope =
    actionsArePreScoped === true ? loopScope : candidate.currentLoopScope;
  return scope?.isConfiguredSourceValid === true ? scope : undefined;
};

/**
 * Whether a picker may skip its root menu and open the trigger fields directly
 * (back arrow disabled): only when the trigger fields would be the root's sole
 * entry. Current Item is a root entry too, so a picker that offers it must
 * keep the root reachable -- the Number/Duration shortcut used to decide this
 * from a loop's DIRECT actions and hid Current Item for branch-in-loop actions.
 */
export const opensTriggerFieldsDirectly = (
  actionMenus: readonly unknown[],
  triggerMenu: unknown | null | undefined,
  offersCurrentItem: boolean,
): boolean =>
  actionMenus.length === 0 &&
  triggerMenu !== null &&
  triggerMenu !== undefined &&
  !offersCurrentItem;
